import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import {
  useCustomerGroupsQuery,
  useCreateCustomerGroupMutation,
  useSaveCustomerGroupMutation,
  useDeleteCustomerGroupMutation
} from "@queries/customers/customer_groups";
import "@styles/opencart-admin.css";

const defaultForm = {
  approval: true,
  sort_order: 0,
  ar_name: "",
  ar_description: "",
  en_name: "",
  en_description: ""
};

export default function CustomerGroupsPage() {
  const [page, setPage] = React.useState(1);
  const pageSize = 20;
  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(defaultForm);

  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, isError, error } =
    useCustomerGroupsQuery({}, page, pageSize);

  const createMutation = useCreateCustomerGroupMutation();
  const saveMutation = useSaveCustomerGroupMutation();
  const deleteMutation = useDeleteCustomerGroupMutation();

  const rows = data?.data || [];
  const total = data?.meta?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  function resetForm() {
    setEditingId(null);
    setForm(defaultForm);
  }

  function openEdit(row) {
    const ar = row.descriptions?.find((d) => d.language_id === 1) || {};
    const en = row.descriptions?.find((d) => d.language_id === 2) || {};
    setEditingId(row.customer_group_id ?? row.id);
    setForm({
      approval: row.approval ? true : false,
      sort_order: row.sort_order ?? 0,
      ar_name: ar.name || "",
      ar_description: ar.description || "",
      en_name: en.name || "",
      en_description: en.description || ""
    });
  }

  async function handleSave(e) {
    e.preventDefault();

    const payload = {
      approval: form.approval ? 1 : 0,
      sort_order: Number(form.sort_order) || 0,
      descriptions: [
        {
          language_id: 1,
          name: form.ar_name,
          description: form.ar_description
        },
        {
          language_id: 2,
          name: form.en_name,
          description: form.en_description
        }
      ]
    };

    if (editingId) {
      await saveMutation.mutateAsync({ id: editingId, payload });
    } else {
      await createMutation.mutateAsync(payload);
    }

    resetForm();
    queryClient.invalidateQueries({
      queryKey: ["customers", "groups"]
    });
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this customer group?")) return;
    await deleteMutation.mutateAsync(id);
    queryClient.invalidateQueries({
      queryKey: ["customers", "groups"]
    });
  }

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>Customer Groups</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Customer Groups</span>
        </nav>
      </div>

      <div className="oc-card">
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>{editingId ? "Edit Customer Group" : "Add Customer Group"}</strong>
          </div>
          <div>
            <button className="oc-btn ghost" onClick={resetForm}>
              New
            </button>
          </div>
        </div>

        <form onSubmit={handleSave}>
          <div className="form-row">
            <div className="form-group">
              <label>Customer Group Name (Arabic)</label>
              <input
                className="oc-input"
                dir="rtl"
                value={form.ar_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, ar_name: e.target.value }))
                }
                required
              />
            </div>
            <div className="form-group">
              <label>Customer Group Name (English)</label>
              <input
                className="oc-input"
                value={form.en_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, en_name: e.target.value }))
                }
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Description (Arabic)</label>
              <textarea
                className="oc-input"
                dir="rtl"
                value={form.ar_description}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    ar_description: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>Description (English)</label>
              <textarea
                className="oc-input"
                value={form.en_description}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    en_description: e.target.value
                  }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Approve New Customers</label>
              <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
                <label>
                  <input
                    type="radio"
                    name="approval"
                    checked={form.approval === true}
                    onChange={() =>
                      setForm((f) => ({ ...f, approval: true }))
                    }
                  />{" "}
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="approval"
                    checked={form.approval === false}
                    onChange={() =>
                      setForm((f) => ({ ...f, approval: false }))
                    }
                  />{" "}
                  No
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Sort Order</label>
              <input
                type="number"
                className="oc-input"
                value={form.sort_order}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    sort_order: e.target.value
                  }))
                }
              />
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "flex-end",
              gap: 8
            }}
          >
            <button
              type="button"
              className="oc-btn ghost"
              onClick={resetForm}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="oc-btn primary"
              disabled={
                createMutation.isLoading || saveMutation.isLoading
              }
            >
              Save
            </button>
          </div>
        </form>
      </div>

      <div className="oc-card" style={{ marginTop: 16 }}>
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>Customer Group List</strong>
          </div>
        </div>

        {isError && (
          <div style={{ color: "#c00", marginBottom: 8 }}>
            Error: {error?.message || "Unknown error"}
          </div>
        )}

        <table className="oc-table">
          <thead>
            <tr>
              <th style={{ width: 70 }}>ID</th>
              <th>Customer Group Name</th>
              <th style={{ width: 120 }}>Sort Order</th>
              <th style={{ width: 140 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: "center", padding: 24 }}>
                  No items
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const ar =
                row.descriptions?.find((d) => d.language_id === 1) || {};
              const en =
                row.descriptions?.find((d) => d.language_id === 2) || {};
              const displayName =
                en.name && ar.name
                  ? `${en.name} (${ar.name})`
                  : en.name || ar.name || "";
              const id = row.customer_group_id ?? row.id;

              return (
                <tr key={id}>
                  <td>{id}</td>
                  <td>{displayName}</td>
                  <td>{row.sort_order}</td>
                  <td>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        className="oc-btn blue"
                        onClick={() => openEdit(row)}
                      >
                        edit
                      </button>
                      <button
                        className="oc-btn danger"
                        onClick={() => handleDelete(id)}
                      >
                        delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        className="oc-card"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 16
        }}
      >
        <div>
          Showing page <strong>{page}</strong> of <strong>{pages}</strong> (
          {total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage(1)}
          >
            « First
          </button>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ‹ Prev
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next ›
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage(pages)}
          >
            Last »
          </button>
        </div>
      </div>
    </div>
  );
}

